import * as React from "react";
import { useContext } from "react";
import { ViewContext } from "components/lib";
import { useFormContext } from "react-hook-form";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { buildSchemaDescriptorOrFallback } from "../utils/schemaDescriptor";

import {
  Step1Schema, Step2Schema, Step3Schema, Step4Schema,
  Step5Schema, Step6Schema, Step7Schema, Step8Schema, Step9Schema, Step10Schema
} from "../utils/schema";

import { cn } from "../../../helpers/cn";
import { useQuoteUi } from "./QuoteUiContext";
import { InitialInformation } from "./intialInformation";
import { Insured } from "./insured";
import { Operations } from "./operations";
import { DataSharing } from "./dataSharing";
import { Drivers } from "./drivers";
import { Vehicles } from "./vehicles";
import { Trailers } from "./trailers";
import { Coverages } from "./coverages";
import { LossesSection } from "./losses";
import { FullSchema, FullSchemaBase } from "../utils/schema";
import { ReviewAndSubmit } from "./ReviewAndSubmit";
import { mapData } from "../utils/mappers/mapQuoteToMongoCollectionSchema";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { Documents } from "./Documents";
import { TargetPriceStep } from "./TargetPriceStep";

export function MultiStepFormLayout({ user, producers, methods }) {
  const {
    state: { currentStep, singlePage, maxStepReached },
    dispatch,
    steps, // Make sure steps config includes the new step 10 (see note below)
  } = useQuoteUi();
  const context = useContext(ViewContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [savingState, setSavingState] = React.useState(false);
  const [submittingState, setSubmittingState] = React.useState(false);
  const [loadingFormState, setLoadingFormState] = React.useState(false);

  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");


  function sanitize(obj) {
    return JSON.parse(JSON.stringify(obj, (_k, v) => {
      if (v instanceof Date) return v.toISOString();
      if (typeof v === "function") return undefined;
      return v;
    }));
  }
  const saveQuote = async (data, location) => {
    setSavingState(true);
    try {
      const agency = producers?.data;
      const postData = mapData(data, agency, user, location, context.notification.show);

      if (location?.state?.data?.quote_id?.length) {
        await axios.patch(`/api/quote/${location.state.data.quote_id}`, postData);
      } else {
        const res = await axios.post("/api/quote", postData);
        const newId = res?.data?.data?.id;
        navigate(location.pathname, {
          state: {
            ...location.state,
            data: { ...location.state.data, quote_id: newId },
          },
          replace: true,
        });
      }

      context.notification.show("Quote Saved", "success", true);
    } catch (err) {
      context.notification.show(
        `There was a problem: ${
          err?.response?.data?.message ?? err?.message ?? String(err)
        }`,
        "warning",
        true
      );
    } finally {
      setSavingState(false);
    }
  };

  // Build step -> fields map for 12 steps.
  // Only include keys that exist in the base schema (step1..step10).
  const SCHEMA_KEYS = React.useMemo(
    () => Object.keys(FullSchemaBase.shape), // ["step1",...,"step10"]
    []
  );

  const STEP_FIELDS = React.useMemo(() => {
    const TOTAL_STEPS = 12;
    const map = {};
    for (let i = 1; i <= TOTAL_STEPS; i++) {
      // up to i, but limited to known schema keys
      const upto = Math.min(i, SCHEMA_KEYS.length);
      map[i] = SCHEMA_KEYS.slice(0, upto);
    }
    return map;
  }, [SCHEMA_KEYS]);

  const values = methods.watch();
  const arraysEqual = React.useCallback(
    (a = [], b = []) =>
      a.length === b.length && a.every((v, i) => v === b[i]),
    []
  );
  const normalizedStep1Carriers = React.useMemo(() => {
    const list = Array.isArray(values?.step1?.carrier)
      ? values.step1.carrier
      : values?.step1?.carrier
      ? [values.step1.carrier]
      : [];
    return list.map((s) => s ?? "");
  }, [values?.step1?.carrier]);
  const enforceProgressiveOnlyWhenMissingOperatingStart = React.useCallback(() => {
    const currentValues = methods.getValues();
    const carriers = Array.isArray(currentValues?.step1?.carrier)
      ? currentValues.step1.carrier
      : currentValues?.step1?.carrier
      ? [currentValues.step1.carrier]
      : [];
    const hasProgressive = carriers.includes("Progressive");
    const hasOtherCarriers = carriers.some((c) => c && c !== "Progressive");
    const hasOperatingStart =
      Boolean(currentValues?.step3?.operatingStart?.year) &&
      Boolean(currentValues?.step3?.operatingStart?.month);

    if (!hasProgressive || !hasOtherCarriers || hasOperatingStart) return carriers;

    const progressiveOnly = ["Progressive"];
    ["step1.carrier", "step2.carrier", "step3.carrier", "step4.carrier"].forEach(
      (path) => {
        methods.setValue(path, progressiveOnly, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    );
    return progressiveOnly;
  }, [methods]);
  const hasAccelerant = React.useMemo(
    () =>
      normalizedStep1Carriers.some(
        (c) => String(c).toLowerCase() === "accelerant"
      ),
    [normalizedStep1Carriers]
  );
  /** Step 4 (Data Sharing) visible when Cover Whale OR Nirvana is selected. */
  const hasNirvana = React.useMemo(
    () => normalizedStep1Carriers.some((c) => c === "Nirvana"),
    [normalizedStep1Carriers]
  );
  const hasTelemetryCarrier = React.useMemo(
    () => normalizedStep1Carriers.some((c) => c === "Cover Whale" || c === "Nirvana"),
    [normalizedStep1Carriers]
  );
  const hasLargeFleet = React.useMemo(
    () => (values?.step6?.vehicles?.length ?? 0) >= 6,
    [values?.step6?.vehicles?.length]
  );
  const visibleSteps = React.useMemo(
    () => {
      let list = steps;
      if (!hasLargeFleet) list = list.filter((s) => s.id !== 10);
      if (!hasAccelerant) list = list.filter((s) => s.id !== 11);
      if (!hasTelemetryCarrier) list = list.filter((s) => s.id !== 4);
      return list;
    },
    [steps, hasAccelerant, hasLargeFleet, hasTelemetryCarrier]
  );
  const currentStepIndex = visibleSteps.findIndex((s) => s.id === currentStep);
  const fallbackStepId = React.useMemo(() => {
    if (!visibleSteps.length) return 1;
    const prior = [...visibleSteps].reverse().find((s) => s.id < currentStep);
    return prior?.id ?? visibleSteps[0].id;
  }, [visibleSteps, currentStep]);
  React.useEffect(() => {
    const currentStep3 = methods.getValues("step3.carrier") ?? [];
    const currentStep4 = methods.getValues("step4.carrier") ?? [];
    if (!arraysEqual(normalizedStep1Carriers, currentStep3)) {
      methods.setValue("step3.carrier", normalizedStep1Carriers, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (!arraysEqual(normalizedStep1Carriers, currentStep4)) {
      methods.setValue("step4.carrier", normalizedStep1Carriers, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }

    if (!hasTelemetryCarrier) {
      const telemetryOption = methods.getValues("step4.telemetryOption");
      const telemetryProvider = methods.getValues("step4.telemetryProvider");
      const hasEld = methods.getValues("step4.hasEld");
      const eldProviderKey = methods.getValues("step4.eldProviderKey");
      const eldProviderOtherText = methods.getValues("step4.eldProviderOtherText");
      const hasDashCam = methods.getValues("step4.hasDashCam");
      const dashCamProviderText = methods.getValues("step4.dashCamProviderText");
      
      const shouldClearOption = telemetryOption !== undefined;
      const shouldClearProvider = telemetryProvider !== undefined;
      const shouldClearHasEld = hasEld !== undefined;
      const shouldClearEldProviderKey = eldProviderKey !== undefined;
      const shouldClearEldProviderOtherText = eldProviderOtherText !== undefined;
      const shouldClearHasDashCam = hasDashCam !== undefined;
      const shouldClearDashCamProviderText = dashCamProviderText !== undefined;

      if (shouldClearOption) {
        methods.setValue("step4.telemetryOption", undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      if (shouldClearProvider) {
        methods.setValue("step4.telemetryProvider", undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      if (shouldClearHasEld) {
        methods.setValue("step4.hasEld", undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      if (shouldClearEldProviderKey) {
        methods.setValue("step4.eldProviderKey", undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      if (shouldClearEldProviderOtherText) {
        methods.setValue("step4.eldProviderOtherText", undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      if (shouldClearHasDashCam) {
        methods.setValue("step4.hasDashCam", undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      if (shouldClearDashCamProviderText) {
        methods.setValue("step4.dashCamProviderText", undefined, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      if (shouldClearOption || shouldClearProvider || shouldClearHasEld || shouldClearEldProviderKey || 
          shouldClearEldProviderOtherText || shouldClearHasDashCam || shouldClearDashCamProviderText) {
        methods.clearErrors([
          "step4.telemetryOption", 
          "step4.telemetryProvider",
          "step4.hasEld",
          "step4.eldProviderKey",
          "step4.eldProviderOtherText",
          "step4.hasDashCam",
          "step4.dashCamProviderText"
        ]);
      }
    }
  }, [arraysEqual, hasTelemetryCarrier, methods, normalizedStep1Carriers]);
  React.useEffect(() => {
    if (currentStepIndex === -1 && visibleSteps.length) {
      dispatch({ type: "SET_STEP", value: fallbackStepId });
    }
  }, [currentStepIndex, visibleSteps, fallbackStepId, dispatch]);

  // Only allow final submit if entire schema is valid
  const canSubmit = FullSchema.safeParse(values ?? {}).success;

  const submitQuote = React.useCallback(async (data) => {
    setSubmittingState(true);
    try {
      const steps = ["step1","step2","step3","step4","step5","step6","step7","step8","step9","step10"];
      const allData = sanitize(
        steps.reduce((acc, s) => ({ ...acc, [s]: data?.[s] ?? {} }), {})
      );

      // 3) Build schema descriptor on the FE
      const schemaDescriptor = buildSchemaDescriptorOrFallback({
        step1: Step1Schema,
        step2: Step2Schema,
        step3: Step3Schema,
        step4: Step4Schema,
        step5: Step5Schema,
        step6: Step6Schema,
        step7: Step7Schema,
        step8: Step8Schema,
        step9: Step9Schema,
        step10: Step10Schema,
      }, allData);
      const agency = producers?.data;
      const postData = mapData(data, agency, user, location, context.notification.show);

      const res = await axios.post("/api/quote/submit", {
        ...postData,
        allData,
        schemaDescriptor,
        ...(location?.state?.data?.quote_id?.length
          ? { id: location?.state?.data?.quote_id }
          : {}),
      });

      const { data: quoteData } = res?.data || {};
      const { status, reason } = quoteData || {};

      if (reason) {
        const numberedReasons = Object.keys(reason)
          .filter((k) => !isNaN(k))
          .map((k) => reason[k]);

        // Extract notes and excluded coverages
        const notes = Array.isArray(reason.notes) ? reason.notes : [];
        const excluded = Array.isArray(reason.excludedCoverages)
          ? reason.excludedCoverages.map(
              (c) => `${c.coverage.toUpperCase()}: ${c.reason}`
            )
          : [];

        // Combine all
        let allReasons = [...numberedReasons, ...notes, ...excluded]
          .filter(Boolean)
          .map((r) =>
            r
              .replace(/Declined(\s+[A-Z]+)?:?\s*/gi, "")
              .trim()
          );

        // Deduplicate
        allReasons = [...new Set(allReasons)];

        const formattedReasons =
          allReasons.length > 1
            ? allReasons.map((r) => `• ${r}`).join("\n")
            : allReasons[0] || "Unknown reason";

        const message = `${status || "Declined"} due to:\n\n${formattedReasons}`;

        setErrorMessage(message);
        setErrorModalOpen(true);
      } else {
        context.notification.show("Quote Submitted", "success", true);
        const reviewUrl = '/app/quote/review';

        navigate(reviewUrl, {
          state: {
            data: {
              ...quoteData?.data,
            },
          },
        });
      }
    } catch (e) {
      console.error("Submission error: ", e);
      const msg = e.response?.data?.errors
        ?.map((er) => (er?.includes(":") ? er.split(":")[1] : er))
        .join(", ");
      setErrorMessage(
        msg || "There was a problem submitting the quote. Please try again later."
      );
      setErrorModalOpen(true);
    } finally {
      setSubmittingState(false);
    }
  }, [location]);


  const onNext = async (currentStep) => {
    const fields = STEP_FIELDS[currentStep];
    if (!fields?.length) return true;
    return methods.trigger(fields, { shouldFocus: true });
  };

  const { handleSubmit } = useFormContext();

  const stepValidity = React.useMemo(() => {
    const map= {};
    visibleSteps.forEach((s) => {
      const fields = STEP_FIELDS[s.id] ?? [];
      if (!fields.length) {
        map[s.id] = true;
        return;
      }
      const slice = Object.fromEntries(
        Object.entries(values ?? {}).filter(([k]) => fields.includes(k))
      );
      const schemaSubset = FullSchemaBase.pick(
        Object.fromEntries(fields.map((f) => [f, true]))
      );
      map[s.id] = schemaSubset.safeParse(slice).success;
    });
    return map;
  }, [visibleSteps, STEP_FIELDS, values]);
  const validSteps = React.useMemo(
    () => visibleSteps.filter((s) => stepValidity[s.id]),
    [stepValidity, visibleSteps]
  );

  const isFirst = currentStepIndex <= 0;
  const isLast = currentStepIndex === visibleSteps.length - 1;
  const nextVisibleStepId =
    currentStepIndex >= 0 && currentStepIndex < visibleSteps.length - 1
      ? visibleSteps[currentStepIndex + 1].id
      : currentStep;
  const prevVisibleStepId =
    currentStepIndex > 0 ? visibleSteps[currentStepIndex - 1].id : currentStep;
  const canProceed = stepValidity[currentStep] ?? true;

  const handleNext = async () => {
    const ok = (await onNext?.(currentStep)) ?? true;
    if (ok) {
      if (currentStep === 3) {
        enforceProgressiveOnlyWhenMissingOperatingStart();
      }
      dispatch({ type: "SET_STEP", value: nextVisibleStepId });
    }
  };

  const handleSave = useCallback(() => {
    enforceProgressiveOnlyWhenMissingOperatingStart();
    const latestValues = methods.getValues();
    saveQuote?.(latestValues, location);
  }, [enforceProgressiveOnlyWhenMissingOperatingStart, location, methods, saveQuote]);
  const handleFinalSubmit = useCallback(() => {
    enforceProgressiveOnlyWhenMissingOperatingStart();
    handleSubmit((data) => submitQuote?.(data))();
  }, [enforceProgressiveOnlyWhenMissingOperatingStart, handleSubmit, submitQuote]);

  // Add the new steps
  const stepComponents = React.useMemo(
    () => ({
      1: <InitialInformation methods={methods} user={user} producers={producers} />,
      2: <Insured methods={methods} agencyData={producers} />,
      3: <Operations />,
      4: <DataSharing />,
      5: <Drivers methods={methods} save={handleSave} />,
      6: <Vehicles />,
      7: <Trailers />,
      8: <Coverages methods={methods} />,
      9: <LossesSection />,
      10: <TargetPriceStep />,
      11: <Documents user={user} />,
      12: <ReviewAndSubmit methods={methods} />
    }),
    [user, producers, methods, handleSave]
  );

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  React.useEffect(() => {
    if (!values) {
      setLoadingFormState(false);
      return;
    };
    if (!steps?.length) {
      setLoadingFormState(false);
      return;
    }

    const lastValidStepId = validSteps[validSteps.length - 1]?.id;
    if (lastValidStepId !== undefined && lastValidStepId !== maxStepReached) {
      dispatch({
        type: "SET_MAX_STEP_REACHED",
        value: lastValidStepId,
      });
    }
    setLoadingFormState(false);
  }, [dispatch, maxStepReached, steps.length, validSteps, values]);

  if (loadingFormState) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-start h-full p-4">
      {/* View toggle */}
      <div className="flex justify-end w-full max-w-7xl mb-4">
        <Button
          variant="outline"
          onClick={() => dispatch({ type: "TOGGLE_SINGLE_PAGE" })}
        >
          {singlePage ? "See As Stepper" : "See As Single Page"}
        </Button>
      </div>

      {/* Stepper header */}
      {!singlePage && (
        <div className="flex items-center gap-6 mb-6 w-full max-w-7xl flex-wrap justify-center">
          {visibleSteps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = idx < currentStepIndex && stepValidity[step.id];
            const prevStepsValid = visibleSteps
              .slice(0, idx)
              .every((s) => stepValidity[s.id]);
            const isClickable = isActive || (step.id <= maxStepReached && prevStepsValid);

            return (
              <div
                key={step.id}
                className={cn(
                  "flex items-center gap-2 cursor-default",
                  isClickable && "cursor-pointer"
                )}
                onClick={() =>
                  isClickable && dispatch({ type: "SET_STEP", value: step.id })
                }
              >
                <div
                  className={cn(
                    "flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors",
                    isActive && "bg-red-600 text-white border-red-600",
                    isCompleted && "bg-green-600 text-white border-green-600 hover:bg-green-700",
                    !isActive && !isCompleted && "border-gray-300 text-gray-400"
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>

                {isActive && (
                  <div className="flex flex-col text-left">
                    <span className="text-sm text-gray-500">
                      Step {idx + 1}/{visibleSteps.length}
                    </span>
                    <span className="text-base font-medium text-gray-900">
                      {step.label}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}


      {/* Body */}
      <Card className="w-full max-w-7xl">
        <CardContent className="p-6 space-y-8">
          {singlePage
            ? visibleSteps.map((s) => (
                <div key={s.id} className="space-y-4">
                  {/* <h2 className="text-lg font-semibold">{s.label}</h2> */}
                  {stepComponents[s.id]}
                </div>
              ))
            : stepComponents[currentStep]}
        </CardContent>
      </Card>

      {/* Footer actions */}
      <div className="flex justify-between md:justify-end mt-8 w-full max-w-7xl gap-2">
        {!singlePage && (
          <>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={savingState || submittingState}
            >
              {savingState ? "Saving..." : "Save"}
            </Button>

            {/* SHOW SUBMIT ONLY ON LAST STEP */}
            {isLast && (
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleFinalSubmit}
                disabled={!canSubmit || submittingState || savingState}
              >
                {submittingState ? "Submitting..." : "Submit"}
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => dispatch({ type: "SET_STEP", value: prevVisibleStepId })}
              className={"min-w-[10ch]"}
              disabled={isFirst || savingState || submittingState}
            >
              Back
            </Button>

            {/* Next disabled on last step (no Next on review page) */}
            {!isLast && (
              <Button
                variant="outline"
                className={canProceed ? "bg-green-600 text-white hover:bg-green-700 hover:text-white" : "bg-gray-800 text-white hover:bg-gray-700 hover:text-white"}
                onClick={handleNext}
                disabled={savingState || submittingState || !canProceed}
              >
                Next Step
              </Button>
            )}
          </>
        )}

        {/* Single page mode: keep as-is, or hide Submit here too if you prefer */}
        {singlePage && (
          <>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={savingState || submittingState}
            >
              {savingState ? "Saving..." : "Save"}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleFinalSubmit}
              disabled={!canSubmit || submittingState || savingState}
            >
              {submittingState ? "Submitting..." : "Submit"}
            </Button>
          </>
        )}
      </div>
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogContent className="max-w-md transition-all duration-200">
          <DialogHeader>
            <DialogTitle className="text-red-600">Submission Failed</DialogTitle>
            <DialogDescription>
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
